import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useFamilyContext } from '../../contexts/FamilyContext';

export default function AuthCallback() {
  const navigate = useNavigate();
  const { refreshFamilyData } = useFamilyContext();
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [error, setError] = useState('');

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        console.log('🔍 Auth Callback Debug - URL:', window.location.href);
        console.log('🔍 Auth Callback Debug - Hash:', window.location.hash);
        
        // Wait a moment for Supabase to process the URL
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Get the current session (Supabase should have processed the URL by now)
        const { data, error } = await supabase.auth.getSession();
        
        console.log('🔍 Auth Callback Debug - Session data:', data);
        console.log('🔍 Auth Callback Debug - Session error:', error);
        
        if (error) {
          console.error('Auth callback error:', error);
          setError(error.message);
          setStatus('error');
          return;
        }

        if (data.session && data.session.user) {
          console.log('✅ Auth Callback Success - User authenticated:', data.session.user.email);
          
          // Refresh family data to get the user's context
          try {
            await refreshFamilyData();
            console.log('✅ Family data refreshed successfully');
          } catch (familyError) {
            console.error('⚠️ Family data refresh failed:', familyError);
            // Don't fail the auth process if family data fails
          }
          
          setStatus('success');
          
          // Redirect to dashboard after a brief success message
          setTimeout(() => {
            navigate('/', { replace: true });
          }, 1500);
          
        } else {
          console.log('❌ No session found, redirecting to login');
          // No session found, redirect to login
          setTimeout(() => {
            navigate('/', { replace: true });
          }, 1000);
        }
      } catch (err) {
        console.error('Auth callback exception:', err);
        setError(err.message || 'Authentication failed');
        setStatus('error');
      }
    };

    // Listen for auth state changes as well
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔍 Auth State Change:', event, session?.user?.email);
      
      if (event === 'SIGNED_IN' && session) {
        console.log('✅ Auth State Change - User signed in via magic link');
        
        try {
          await refreshFamilyData();
          console.log('✅ Family data refreshed successfully');
        } catch (familyError) {
          console.error('⚠️ Family data refresh failed:', familyError);
        }
        
        setStatus('success');
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 1500);
      } else if (event === 'SIGNED_OUT') {
        console.log('❌ Auth State Change - User signed out');
        navigate('/', { replace: true });
      }
    });

    // Start the callback handling
    handleAuthCallback();
    
    // Cleanup subscription
    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, refreshFamilyData]);

  if (status === 'processing') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-azure-100 via-violet-50 to-pink-100">
        <div className="max-w-md w-full space-y-8 bg-neutral-50 p-8 rounded-xl shadow-lg">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <img 
                src="/papersedge-icon-transparent.png" 
                alt="Paper Sedge" 
                className="w-12 h-12"
              />
            </div>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <h2 className="text-2xl font-bold text-stone-900 mb-2">
              Signing you in...
            </h2>
            <p className="text-stone-600">
              Please wait while we complete your authentication.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-azure-100 via-violet-50 to-pink-100">
        <div className="max-w-md w-full space-y-8 bg-neutral-50 p-8 rounded-xl shadow-lg">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <img 
                src="/papersedge-icon-transparent.png" 
                alt="Paper Sedge" 
                className="w-12 h-12"
              />
            </div>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 mb-4">
              <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-stone-900 mb-2">
              Welcome back!
            </h2>
            <p className="text-stone-600">
              You've been successfully signed in. Redirecting to your dashboard...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-azure-100 via-violet-50 to-pink-100">
        <div className="max-w-md w-full space-y-8 bg-neutral-50 p-8 rounded-xl shadow-lg">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <img 
                src="/papersedge-icon-transparent.png" 
                alt="Paper Sedge" 
                className="w-12 h-12"
              />
            </div>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-stone-900 mb-2">
              Authentication Failed
            </h2>
            <p className="text-stone-600 mb-6">
              {error || 'There was a problem signing you in. Please try again.'}
            </p>
            
            <button
              onClick={() => navigate('/', { replace: true })}
              className="w-full py-3 px-4 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
} 